// frontend/src/components/fleet/VehicleSilhouette.jsx
//
// Siluetas por TIPO de vehículo.
//
// El componente no conoce ningún vehículo concreto: resuelve la silueta a partir
// de `vehicle.type` contra un registro (`SILHOUETTES`) y cae en una silueta
// genérica si el tipo no está registrado. Añadir una silueta nueva —las que
// diseñe Luis— es agregar una entrada al registro (o un archivo SVG en
// `public/silhouettes/<tipo>.svg`, que tiene prioridad si existe), sin tocar la
// lógica de ninguna vista.
//
// Se usa en la vista lista, en las tarjetas de vehículo, en el detalle y en las
// impresiones.

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, useTheme } from '@mui/material';

// Ruta pública donde se pueden dejar siluetas diseñadas sin recompilar.
const EXTERNAL_SILHOUETTE_DIR = '/silhouettes';

/** Normaliza el tipo para buscar en el registro. */
export const normalizeVehicleType = (type) =>
  String(type || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-');

// Alias frecuentes → clave canónica del registro.
const TYPE_ALIASES = {
  camion: 'truck',
  'camión': 'truck',
  tractocamion: 'tractor',
  'tractocamión': 'tractor',
  tracto: 'tractor',
  quinta: 'tractor',
  remolque: 'trailer',
  semirremolque: 'semi-trailer',
  semi: 'semi-trailer',
  autobus: 'bus',
  'autobús': 'bus',
  camioneta: 'pickup',
  pick: 'pickup',
  'pick-up': 'pickup',
  volteo: 'dump',
  minero: 'dump',
  dumper: 'dump',
  van: 'van',
  vagoneta: 'van',
};

// Cada silueta es un path sobre un lienzo 120x48. Trazos simples y llenos,
// pensados para leerse a 32 px de alto en una lista densa.
const SILHOUETTES = {
  truck: {
    label: 'Camión',
    viewBox: '0 0 120 48',
    paths: [
      'M4 32 L4 12 L62 12 L62 32 Z',
      'M64 32 L64 18 L86 18 L98 28 L98 32 Z',
      'M4 32 L114 32 L114 36 L4 36 Z',
    ],
    wheels: [[22, 38], [78, 38], [98, 38]],
  },
  tractor: {
    label: 'Tractocamión',
    viewBox: '0 0 120 48',
    paths: [
      'M6 32 L6 20 L30 20 L40 28 L40 32 Z',
      'M44 32 L44 14 L114 14 L114 32 Z',
    ],
    wheels: [[16, 38], [36, 38], [86, 38], [100, 38]],
  },
  trailer: {
    label: 'Remolque',
    viewBox: '0 0 120 48',
    paths: ['M8 32 L8 14 L112 14 L112 32 Z', 'M8 32 L112 32 L112 35 L8 35 Z'],
    wheels: [[74, 38], [92, 38]],
  },
  'semi-trailer': {
    label: 'Semirremolque',
    viewBox: '0 0 120 48',
    paths: ['M4 32 L4 12 L112 12 L112 32 Z', 'M14 32 L14 40 L18 40 L18 32 Z'],
    wheels: [[70, 38], [86, 38], [102, 38]],
  },
  bus: {
    label: 'Autobús',
    viewBox: '0 0 120 48',
    paths: [
      'M6 32 L6 12 Q6 8 12 8 L108 8 Q114 8 114 12 L114 32 Z',
      'M12 14 L52 14 L52 22 L12 22 Z',
      'M58 14 L108 14 L108 22 L58 22 Z',
    ],
    wheels: [[26, 38], [96, 38]],
  },
  van: {
    label: 'Van',
    viewBox: '0 0 120 48',
    paths: [
      'M8 32 L8 16 L70 16 L70 32 Z',
      'M70 32 L70 20 L92 20 L104 30 L104 32 Z',
    ],
    wheels: [[28, 38], [90, 38]],
  },
  pickup: {
    label: 'Pickup',
    viewBox: '0 0 120 48',
    paths: [
      'M10 32 L10 24 L34 24 L44 16 L70 16 L76 24 L76 32 Z',
      'M76 32 L76 22 L112 22 L112 32 Z',
    ],
    wheels: [[32, 38], [96, 38]],
  },
  dump: {
    label: 'Volteo / minero',
    viewBox: '0 0 120 48',
    paths: [
      'M6 32 L6 20 L28 20 L38 28 L38 32 Z',
      'M42 32 L52 12 L112 12 L112 32 Z',
    ],
    wheels: [[18, 38], [62, 38], [84, 38], [104, 38]],
  },
  other: {
    label: 'Equipo',
    viewBox: '0 0 120 48',
    paths: ['M10 32 L10 16 L110 16 L110 32 Z'],
    wheels: [[30, 38], [90, 38]],
  },
};

export const FALLBACK_TYPE = 'other';

/** Clave de silueta para un tipo dado (con alias y respaldo). */
export const resolveSilhouetteKey = (type) => {
  const normalized = normalizeVehicleType(type);
  if (!normalized) return FALLBACK_TYPE;
  if (SILHOUETTES[normalized]) return normalized;
  const alias = TYPE_ALIASES[normalized];
  if (alias && SILHOUETTES[alias]) return alias;
  return FALLBACK_TYPE;
};

export const getSilhouetteLabel = (type) => SILHOUETTES[resolveSilhouetteKey(type)].label;

// Caché de disponibilidad de SVGs externos, para no pedir el mismo 404 en bucle.
const externalAvailability = new Map();

const checkExternal = (key) => {
  if (externalAvailability.has(key)) return externalAvailability.get(key);
  const promise = fetch(`${EXTERNAL_SILHOUETTE_DIR}/${key}.svg`, { method: 'HEAD' })
    .then((res) => res.ok)
    .catch(() => false);
  externalAvailability.set(key, promise);
  return promise;
};

const VehicleSilhouette = ({ type, height = 32, color, title, sx = {} }) => {
  const theme = useTheme();
  const key = resolveSilhouetteKey(type);
  const spec = SILHOUETTES[key];
  const tone = color || theme.palette.text.secondary;
  const [externalSrc, setExternalSrc] = useState(null);

  useEffect(() => {
    let cancelled = false;
    checkExternal(key).then((available) => {
      if (!cancelled) setExternalSrc(available ? `${EXTERNAL_SILHOUETTE_DIR}/${key}.svg` : null);
    });
    return () => { cancelled = true; };
  }, [key]);

  const label = title || spec.label;

  if (externalSrc) {
    return (
      <Box
        component="img"
        src={externalSrc}
        alt={label}
        title={label}
        sx={{ height, width: 'auto', maxWidth: '100%', display: 'block', ...sx }}
      />
    );
  }

  return (
    <Box
      component="svg"
      viewBox={spec.viewBox}
      role="img"
      aria-label={label}
      sx={{ height, width: 'auto', display: 'block', flexShrink: 0, ...sx }}
    >
      <title>{label}</title>
      {spec.paths.map((d, index) => (
        <path key={index} d={d} fill={tone} opacity={index === 0 ? 0.95 : 0.75} />
      ))}
      {(spec.wheels || []).map(([cx, cy], index) => (
        <circle key={`w-${index}`} cx={cx} cy={cy} r={5} fill={tone} />
      ))}
    </Box>
  );
};

VehicleSilhouette.propTypes = {
  type: PropTypes.string,
  height: PropTypes.number,
  color: PropTypes.string,
  title: PropTypes.string,
  sx: PropTypes.object,
};

export default VehicleSilhouette;
